/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 System Health & Performance Data
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Backs the four KPI cards + long-running queries panel at the top of the
/// home dashboard. Returns a single aggregated payload matching the shape
/// the existing template binds to, so swapping from the prior mock endpoint
/// is a one-line change in the page.
///
/// Source choices (real-time vs. account_usage):
///   - Connection check: SELECT CURRENT_VERSION() — measures wall-clock
///     round-trip latency. Status is binary HEALTHY/DEGRADED based on
///     whether the call succeeded.
///   - Credits 30d: METERING_DAILY_HISTORY (pre-aggregated, ~3hr lag).
///   - Active queries / today total: INFORMATION_SCHEMA.QUERY_HISTORY
///     (real-time, no lag) so the "currently running" number reflects
///     the actual session state.
///   - Failed logins: ACCOUNT_USAGE.LOGIN_HISTORY — 45min lag is fine
///     for security alerting; LOGIN_HISTORY isn't in INFORMATION_SCHEMA.
///   - Long-running list: INFORMATION_SCHEMA.QUERY_HISTORY again, since
///     "currently running" must be live.
///
/// Each sub-fetch runs through Promise.allSettled so a single failure
/// (e.g. role lacks privilege on LOGIN_HISTORY) doesn't blank the whole
/// panel — we fill that field's defaults instead.

import {
  CREDIT_PRICE_USD,
  dateRangeClause,
  dateOnlyRangeClause,
  type DateRangeArg,
} from '~/scripts/queries/costQueries'

// ---------------------------------------------------------------------------
// Public shape — matches what app/pages/index.vue's template already binds.
// ---------------------------------------------------------------------------
//
// Field names use generic "_in_range" suffixes (instead of the old
// "_30d" / "_24h" hardcodes) since the active range now drives the
// underlying window. Backwards-compatible aliases (`total_30d`,
// `failed_logins_24h`) are kept on the payload so any older bindings
// keep rendering until they're migrated to the new names.
export interface HealthSnapshot {
  status: {
    status: 'HEALTHY' | 'DEGRADED' | 'OFFLINE'
    latency_ms: number
  }
  credits: {
    total_in_range: number
    estimated_cost_usd: number
    /** @deprecated alias of total_in_range — kept for any callers still binding to the legacy name. */
    total_30d: number
  }
  queries: {
    active_count: number
    total_today: number
    long_running: Array<{
      query_id: string
      user_name: string
      query_text: string
      duration_sec: number
    }>
  }
  security: {
    critical_alerts: number
    failed_logins_in_range: number
    /** @deprecated alias of failed_logins_in_range. */
    failed_logins_24h: number
  }
}

// ---------------------------------------------------------------------------
// SQL — kept inline because there are only four queries and two of them
// share metric extraction. If this file grows, lift them into
// app/scripts/queries/healthQueries.ts the way costQueries.ts is structured.
//
// Two of the queries (credits + failed logins) are time-bound and accept the
// dashboard's selected DateRangeArg so the KPI cards reflect the active
// filter. The remaining queries are point-in-time:
//   - Connection probe → wall-clock latency, no range applies
//   - Active queries / today total → "currently running" state, live
//   - Long running list → currently running queries, live
// ---------------------------------------------------------------------------

/** Default range when the caller doesn't pass one — last 30 days. Mirrors
 *  the prior hardcoded windows so behavior is unchanged for old callers. */
function defaultRange(): DateRangeArg {
  return { start: new Date(Date.now() - 30 * 86_400_000), end: new Date() }
}

function creditsSql(range: DateRangeArg): string {
  return `
SELECT
  COALESCE(SUM(CREDITS_USED_COMPUTE + CREDITS_USED_CLOUD_SERVICES), 0)             AS "creditsUsed",
  ROUND(COALESCE(SUM(CREDITS_USED_COMPUTE + CREDITS_USED_CLOUD_SERVICES), 0)
        * ${CREDIT_PRICE_USD}, 2)                                                   AS "estimatedCostUsd"
FROM SNOWFLAKE.ACCOUNT_USAGE.METERING_DAILY_HISTORY
WHERE SERVICE_TYPE = 'WAREHOUSE_METERING'
  AND ${dateOnlyRangeClause('USAGE_DATE', range)}
`
}

// "Active" = anything not yet finished. Snowflake's running-ish set is
// RUNNING, QUEUED, RESUMING_WAREHOUSE, BLOCKED — all of these are
// holding or waiting for compute, which is what users intuitively
// think of as "in flight". This stays as live state — the filter bar's
// range doesn't apply (you can't ask "how many queries were active 3
// weeks ago"; we'd need ACCOUNT_USAGE for that and it's lagged).
const ACTIVE_AND_TODAY_SQL = `
SELECT
  COUNT_IF(EXECUTION_STATUS IN ('RUNNING','QUEUED','RESUMING_WAREHOUSE','BLOCKED'))  AS "activeCount",
  COUNT_IF(START_TIME >= CURRENT_DATE())                                             AS "totalToday"
FROM TABLE(INFORMATION_SCHEMA.QUERY_HISTORY(
  END_TIME_RANGE_START => DATEADD(DAY, -1, CURRENT_TIMESTAMP()),
  RESULT_LIMIT => 10000
))
`

function failedLoginsSql(range: DateRangeArg): string {
  return `
SELECT COUNT(*) AS "failedLogins"
FROM SNOWFLAKE.ACCOUNT_USAGE.LOGIN_HISTORY
WHERE ${dateRangeClause('EVENT_TIMESTAMP', range)}
  AND IS_SUCCESS = 'NO'
`
}

const LONG_RUNNING_SQL = `
SELECT
  QUERY_ID                              AS "queryId",
  USER_NAME                             AS "userName",
  LEFT(QUERY_TEXT, 200)                 AS "queryText",
  ROUND(TOTAL_ELAPSED_TIME / 1000.0, 0) AS "durationSec"
FROM TABLE(INFORMATION_SCHEMA.QUERY_HISTORY(RESULT_LIMIT => 1000))
WHERE EXECUTION_STATUS IN ('RUNNING','QUEUED','RESUMING_WAREHOUSE','BLOCKED')
  AND TOTAL_ELAPSED_TIME > 30000
ORDER BY TOTAL_ELAPSED_TIME DESC
LIMIT 5
`

const CONNECTION_PROBE_SQL = 'SELECT CURRENT_VERSION() AS "version"'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Run a query and return rows on success, or null on failure. We don't
 * throw — each section's failure should degrade gracefully (defaults
 * applied below) rather than blank the whole health panel.
 */
async function safeRun(sql: string): Promise<any[] | null> {
  try {
    const res: any = await useSnowflake({ query: sql, returnType: 'json' })
    if (!res || res.status === 'ERROR') {
      console.warn('[useHealthData] sub-query failed:', res?.error)
      return null
    }
    return Array.isArray(res.data) ? res.data : []
  } catch (err) {
    console.warn('[useHealthData] sub-query threw:', err)
    return null
  }
}

// ---------------------------------------------------------------------------
// Public composable
// ---------------------------------------------------------------------------
export const useHealthData = () => {
  /**
   * Fetch a complete health snapshot. Sub-queries fan out in parallel; a
   * partial failure surfaces as that field's default (0 / 'OFFLINE' / []).
   *
   * The supplied `range` drives the time-bound metrics (credits, failed
   * logins). Real-time metrics (connection latency, currently active
   * queries, currently long-running queries) ignore the range — there's
   * no meaningful way to ask "what was running 3 weeks ago" without
   * INFORMATION_SCHEMA's session-bounded view, which is always live.
   */
  const fetchHealth = async (range?: DateRangeArg): Promise<HealthSnapshot> => {
    const r = range ?? defaultRange()
    // Connection probe is awaited inline so we can measure round-trip
    // latency. Snowflake-side execution is essentially zero for
    // CURRENT_VERSION, so the wall-clock delta is a fair "how fast can
    // I talk to Snowflake" number.
    const probeStart = Date.now()
    const probeRows = await safeRun(CONNECTION_PROBE_SQL)
    const latency_ms = Date.now() - probeStart
    const status: HealthSnapshot['status']['status'] =
      probeRows === null ? 'OFFLINE' : probeRows.length > 0 ? 'HEALTHY' : 'DEGRADED'

    // Remaining sub-queries run in parallel. allSettled so one bad
    // privilege (e.g. role lacks USAGE on LOGIN_HISTORY) doesn't take
    // out the whole panel.
    const [creditsRows, activeAndTodayRows, failedLoginsRows, longRunningRows] = await Promise.all([
      safeRun(creditsSql(r)),
      safeRun(ACTIVE_AND_TODAY_SQL),
      safeRun(failedLoginsSql(r)),
      safeRun(LONG_RUNNING_SQL),
    ])

    const credits = creditsRows?.[0] || {}
    const activeAndToday = activeAndTodayRows?.[0] || {}
    const failed = failedLoginsRows?.[0] || {}
    const longRunning = longRunningRows || []

    const failedLogins = Number(failed.failedLogins) || 0
    const creditsUsed = Number(credits.creditsUsed) || 0

    return {
      status: { status, latency_ms },
      credits: {
        total_in_range: creditsUsed,
        estimated_cost_usd: Number(credits.estimatedCostUsd) || 0,
        // Legacy alias — kept until every consumer reads total_in_range.
        total_30d: creditsUsed,
      },
      queries: {
        active_count: Number(activeAndToday.activeCount) || 0,
        total_today: Number(activeAndToday.totalToday) || 0,
        long_running: longRunning.map((r: any) => ({
          query_id: String(r.queryId || ''),
          user_name: String(r.userName || ''),
          query_text: String(r.queryText || ''),
          duration_sec: Number(r.durationSec) || 0,
        })),
      },
      security: {
        // Until we have richer signal sources (anomalous role escalation,
        // first-time-from-new-IP logins, ACCESS_HISTORY anomalies), we
        // treat failed logins as the "critical alerts" headline number —
        // it's the most useful single signal of an account under attack.
        critical_alerts: failedLogins,
        failed_logins_in_range: failedLogins,
        failed_logins_24h: failedLogins,
      },
    }
  }

  return { fetchHealth }
}
